/**
 * The group control (§B): the design system's Segmented with its sunrise.
 *
 * Desktop: inactive items are 52 px icon-only columns and the active item
 * takes the rest with icon + label. Columns switch at once so the Sunrise can
 * measure its final box; the icons glide to their new places (FLIP, 420 drift,
 * WAAPI — no React state per frame) and the label fades in (240 settle, 120 in).
 * Inactive items show a calm tooltip with the full group name.
 *
 * Touch (mobile, kiosk): four equal columns, icon stacked over label, no tooltips.
 */
import { useLayoutEffect, useMemo, useRef, type CSSProperties, type JSX, type PointerEvent as ReactPointerEvent } from "react";
import { DUR, EASE_CSS, Segmented } from "@/design";
import type { GroupId } from "@/contracts/data";
import { GROUPS } from "@/contracts/taxonomy";

export type PanelMode = "desktop" | "kiosk" | "mobile";

// Flush inside the panel glass, like LightIsland's inner Segmented: no second glass layer.
const SEG_BASE: CSSProperties = {
  boxShadow: "none",
  background: "color-mix(in srgb, var(--night) 16%, transparent)",
  backdropFilter: "none",
  WebkitBackdropFilter: "none",
};

const isGroupId = (k: string): k is GroupId => k === "A" || k === "B" || k === "C" || k === "D";

export interface GroupControlProps {
  value: GroupId;
  onChange: (g: GroupId) => void;
  mode: PanelMode;
  reduced: boolean;
}

export function GroupControl({ value, onChange, mode, reduced }: GroupControlProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const touch = mode !== "desktop";

  const items = useMemo(() => GROUPS.map((g) => ({ key: g.id, icon: g.icon, label: g.short })), []);
  const style = useMemo<CSSProperties>(
    () => ({
      ...SEG_BASE,
      gridTemplateColumns: touch ? "repeat(4, minmax(0, 1fr))" : GROUPS.map((g) => (g.id === value ? "minmax(0, 1fr)" : "var(--ld-loc-seg-off)")).join(" "),
    }),
    [touch, value],
  );

  // Icon x positions (relative to the control) after the last layout, for FLIP.
  const lastX = useRef<Map<string, number> | null>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const root = wrap?.querySelector<HTMLElement>('[role="radiogroup"]');
    if (!wrap || !root) return undefined;

    const measure = (): { x: Map<string, number>; icons: Array<[string, SVGSVGElement]> } => {
      const base = root.getBoundingClientRect().left;
      const x = new Map<string, number>();
      const icons: Array<[string, SVGSVGElement]> = [];
      root.querySelectorAll<HTMLElement>("button[data-key]").forEach((b) => {
        const svg = b.querySelector("svg");
        const k = b.dataset.key;
        if (!svg || !k) return;
        for (const a of svg.getAnimations?.() ?? []) a.cancel();
        x.set(k, svg.getBoundingClientRect().left - base);
        icons.push([k, svg]);
      });
      return { x, icons };
    };

    // Tooltip anchors: centre of each item.
    const placeTips = () => {
      wrap.querySelectorAll<HTMLElement>(".ld-loc-panel-gtip").forEach((tip) => {
        const b = root.querySelector<HTMLElement>(`button[data-key="${tip.dataset.for}"]`);
        if (b) tip.style.left = `${b.offsetLeft + b.offsetWidth / 2 + root.offsetLeft}px`;
      });
    };

    // Names are the full group labels (§H.2). The desktop hides inactive labels,
    // which would otherwise leave those radios without a name. Segmented leaves
    // aria-label unset when an item has a label, so React never touches it.
    root.querySelectorAll<HTMLElement>("button[data-key]").forEach((b) => {
      const g = GROUPS.find((x) => x.id === b.dataset.key);
      if (g) b.setAttribute("aria-label", g.label);
    });

    const { x, icons } = measure();
    const prev = lastX.current;
    lastX.current = x;
    placeTips();
    if (prev && !touch && !reduced) {
      for (const [k, svg] of icons) {
        const from = prev.get(k);
        const to = x.get(k);
        if (from == null || to == null) continue;
        const dx = from - to;
        if (Math.abs(dx) > 0.5 && typeof svg.animate === "function") {
          svg.animate([{ transform: `translateX(${dx}px)` }, { transform: "translateX(0)" }], { duration: DUR.calm, easing: EASE_CSS.drift });
        }
      }
    }

    // Keep the recorded positions true across resizes and late font loads (no animation).
    let alive = true;
    const remeasure = () => {
      if (!alive) return;
      lastX.current = measure().x;
      placeTips();
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(remeasure) : null;
    ro?.observe(root);
    void document.fonts?.ready.then(remeasure);
    return () => {
      alive = false;
      ro?.disconnect();
    };
  }, [value, touch, reduced]);

  // Tooltips: light class toggles on pointer, no React state.
  const tipFor = (key: string | null) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    wrap.querySelectorAll<HTMLElement>(".ld-loc-panel-gtip").forEach((t) => t.classList.toggle("is-on", t.dataset.for === key && key !== value));
  };
  const onOver = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (touch || e.pointerType !== "mouse") return;
    const b = (e.target as Element).closest<HTMLElement>("button[data-key]");
    tipFor(b?.dataset.key ?? null);
  };

  return (
    <div
      ref={wrapRef}
      className="ld-loc-panel-gwrap"
      onPointerOver={onOver}
      onPointerLeave={() => tipFor(null)}
      onPointerDown={() => tipFor(null)}
    >
      <Segmented
        items={items}
        value={value}
        onChange={(k) => {
          tipFor(null);
          if (isGroupId(k) && k !== value) onChange(k);
        }}
        label="Place groups"
        className={touch ? `ld-loc-panel-groups is-touch is-${mode}` : "ld-loc-panel-groups"}
        style={style}
      />
      {!touch &&
        GROUPS.map((g) => (
          <span key={g.id} className="ld-tip ld-tip--up ld-loc-panel-gtip" data-for={g.id} aria-hidden="true">
            {g.label}
          </span>
        ))}
    </div>
  );
}
